import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Clock, MapPin, Mountain, Navigation, 
  CheckCircle2, Circle, ChevronRight, Star, ExternalLink 
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from '@/contexts/LocationContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

const ItineraryDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, getLocalizedField, language } = useLanguage();
  const { location } = useLocation();
  
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStop, setActiveStop] = useState(0);
  const [completedStops, setCompletedStops] = useState(new Set());

  const loadItinerary = useCallback(async () => {
    try {
      const res = await api.getItinerary(id, location?.lat, location?.lng);
      setItinerary(res.data);
    } catch (error) {
      console.error('Error loading itinerary:', error);
    } finally {
      setLoading(false);
    }
  }, [id, location]);

  useEffect(() => {
    loadItinerary();
  }, [loadItinerary]);

  const toggleStopComplete = (index) => {
    setCompletedStops(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const openDirections = (place) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${place.latitude},${place.longitude}`;
    window.open(url, '_blank');
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      easy: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      hard: 'bg-red-100 text-red-700',
    };
    return colors[difficulty] || colors.medium;
  };

  const difficultyLabels = {
    easy: { es: 'Fácil', en: 'Easy', de: 'Einfach' },
    medium: { es: 'Moderado', en: 'Moderate', de: 'Mittel' },
    hard: { es: 'Difícil', en: 'Difficult', de: 'Schwer' },
  };

  if (loading) {
    return (
      <div className="pb-20">
        <Skeleton className="h-64 w-full" />
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!itinerary) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Itinerario no encontrado</p>
      </div>
    );
  }

  const name = getLocalizedField(itinerary, 'name');
  const description = getLocalizedField(itinerary, 'description');
  const progress = (completedStops.size / itinerary.stops.length) * 100;

  return (
    <div className="pb-20" data-testid="itinerary-detail-page">
      {/* Hero */}
      <div className="relative h-64 md:h-80">
        <img
          src={itinerary.image}
          alt={name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute safe-top-offset left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
        </button>
        
        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <Badge className={`mb-3 ${getDifficultyColor(itinerary.difficulty)}`}>
            <Mountain size={14} className="mr-1" />
            {difficultyLabels[itinerary.difficulty]?.[language] || itinerary.difficulty}
          </Badge>
          
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold mb-2">
            {name}
          </h1>
          
          <div className="flex items-center gap-4 text-white/90">
            <div className="flex items-center gap-1">
              <Clock size={16} />
              <span>{itinerary.duration}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin size={16} />
              <span>{itinerary.distance_km} km</span>
            </div>
            <div className="flex items-center gap-1">
              <span>{itinerary.stops.length} paradas</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Description */}
        <div className="bg-[#38B2AC]/5 rounded-2xl p-5 mb-6">
          <p className="text-gray-700 leading-relaxed">{description}</p>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-[#1D1D1F]">Tu progreso</span>
            <span className="text-sm text-gray-500">
              {completedStops.size} de {itinerary.stops.length} completadas
            </span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#38B2AC] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Stops */}
        <div className="space-y-4">
          <h2 className="font-semibold text-lg text-[#1D1D1F]">Paradas del itinerario</h2>
          
          {itinerary.stops.map((stop, index) => {
            const place = stop.place;
            if (!place) return null;
            
            const placeName = getLocalizedField(place, 'name');
            const tip = getLocalizedField(stop, 'tip');
            const isCompleted = completedStops.has(index);
            const isActive = activeStop === index;
            
            return (
              <div 
                key={stop.place_id}
                className={`bg-white rounded-2xl shadow-sm border-2 transition-all ${
                  isActive ? 'border-[#38B2AC]' : 'border-transparent'
                } ${isCompleted ? 'bg-green-50' : ''}`}
                data-testid={`stop-${index}`}
              >
                {/* Stop header */}
                <div 
                  className="flex items-center gap-4 p-4 cursor-pointer"
                  onClick={() => setActiveStop(isActive ? -1 : index)}
                >
                  {/* Step number / check */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleStopComplete(index);
                    }}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      isCompleted 
                        ? 'bg-green-500 text-white' 
                        : 'bg-[#38B2AC]/10 text-[#38B2AC]'
                    }`}
                    data-testid={`complete-btn-${index}`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 size={20} />
                    ) : (
                      <span className="font-bold">{index + 1}</span>
                    )}
                  </button>
                  
                  {/* Place image */}
                  <img
                    src={place.images?.[0] || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=200'}
                    alt={placeName}
                    className="w-16 h-16 rounded-xl object-cover"
                  />
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-semibold text-[#1D1D1F] ${isCompleted ? 'line-through opacity-60' : ''}`}>
                      {placeName}
                    </h3>
                    {place.google_rating && (
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Star size={14} className="text-yellow-500" fill="currentColor" />
                        <span>{place.google_rating}</span>
                      </div>
                    )}
                    {place.distance !== undefined && (
                      <div className="flex items-center gap-1 text-sm text-[#38B2AC]">
                        <MapPin size={14} />
                        <span>
                          {place.distance < 1 
                            ? `${Math.round(place.distance * 1000)} m` 
                            : `${place.distance} km`}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <ChevronRight 
                    size={20} 
                    className={`text-gray-400 transition-transform ${isActive ? 'rotate-90' : ''}`} 
                  />
                </div>
                
                {/* Expanded content */}
                {isActive && (
                  <div className="px-4 pb-4 pt-0 border-t">
                    <div className="pt-4 space-y-4">
                      {/* Tip */}
                      {tip && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                          <p className="text-sm text-yellow-800">
                            <strong>💡 Consejo:</strong> {tip}
                          </p>
                        </div>
                      )}
                      
                      {/* Place description */}
                      <p className="text-gray-600 text-sm">
                        {getLocalizedField(place, 'description')}
                      </p>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => openDirections(place)}
                          className="flex-1 bg-[#38B2AC] hover:bg-[#005A63] rounded-full"
                          data-testid={`directions-btn-${index}`}
                        >
                          <Navigation size={16} className="mr-2" />
                          Cómo llegar
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/place/${place.id}`)}
                          className="border-[#38B2AC] text-[#38B2AC] hover:bg-[#38B2AC]/10 rounded-full"
                          data-testid={`details-btn-${index}`}
                        >
                          <ExternalLink size={16} className="mr-2" />
                          Ver más
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Completion message */}
        {completedStops.size === itinerary.stops.length && (
          <div className="mt-8 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl p-6 text-center">
            <CheckCircle2 size={48} className="mx-auto mb-4" />
            <h3 className="font-['Playfair_Display'] text-2xl font-bold mb-2">
              ¡Itinerario completado!
            </h3>
            <p className="text-white/90">
              Has visitado todas las paradas. ¡Esperamos que hayas disfrutado de la experiencia!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItineraryDetailPage;
